import { useState } from "react"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { useRunTemplateMutation } from "@/modules/userbot/store"
import { useUserbotWriteAccess } from "@/modules/userbot/hooks"
import { getChainerError } from "@/modules/userbot/components/clients/chainerError"
import { normalizeTelegramLink } from "@/modules/userbot/components/massOps/telegramLink"
import { CHAT_EXPORT_TEMPLATE_ID } from "@/modules/userbot/constants"

interface ChatExportFormProps {
  onStarted?: () => void
}

/**
 * Форма «Собрать чат через юзербота»: username/ссылка → запуск шаблона
 * chat-export (role: chat-export), весь чат целиком (limit не отправляется).
 * Рендерится только при наличии прав записи в модуль userbot.
 */
export function ChatExportForm({ onStarted }: ChatExportFormProps) {
  const canWrite = useUserbotWriteAccess()
  const [runTemplate, { isLoading }] = useRunTemplateMutation()
  const [value, setValue] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [started, setStarted] = useState(false)

  if (!canWrite) return null

  const handleSubmit = async () => {
    setError(null)
    setStarted(false)
    const chat = normalizeTelegramLink(value)
    if (!chat) {
      setError("Введите @username или ссылку на чат (t.me/...)")
      return
    }
    try {
      await runTemplate({
        id: CHAT_EXPORT_TEMPLATE_ID,
        body: { inputs: { chat }, role: "chat-export" },
      }).unwrap()
      setStarted(true)
      setValue("")
      onStarted?.()
    } catch (e) {
      setError(getChainerError(e).message)
    }
  }

  return (
    <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="subtitle2" gutterBottom>
        Собрать чат через юзербота
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          label="@username или ссылка на чат"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleSubmit()
          }}
          error={!!error}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          Собрать
          {isLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
        </Button>
      </Stack>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}
      {started && (
        <Alert severity="success" sx={{ mt: 1 }}>
          Сбор запущен, чат появится в библиотеке после завершения
        </Alert>
      )}
    </Box>
  )
}
